import type {
  Configuration,
  PublicClientApplication,
} from '@azure/msal-browser'
import { useRef, useState } from 'react'

import { env } from '@/env'
import useTranslator from '@/hooks/translation'
import { getSsoProvider } from '@/services/fintalk/services/fintalk-backoffice/auth/get-provider-sso'
import { ssoLogin } from '@/services/fintalk/services/fintalk-backoffice/auth/sso'

import { handleAuthError } from '../utils/handle-auth-error'
import { useAuthSession } from './use-auth-session'

type SsoLoginError = {
  type: 'error'
  errorType: 'NETWORK_ERROR' | 'CLIENT_ERROR' | 'UNKNOWN'
  eventId?: string
}

type SsoLoginState =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'success' }
  | SsoLoginError

export const useSsoLogin = () => {
  const { getTranslation } = useTranslator()
  const { enterSession } = useAuthSession()

  const [state, setState] = useState<SsoLoginState>({ type: 'idle' })

  const msalInstanceRef = useRef<PublicClientApplication | null>(null)

  const getMsalInstance = async () => {
    if (!msalInstanceRef.current) {
      const { PublicClientApplication } = await import('@azure/msal-browser')

      const config: Configuration = {
        auth: {
          clientId: env.VITE_AZURE_CLIENT_ID,
          authority: `https://login.microsoftonline.com/${env.VITE_AZURE_TENANT_ID}`,
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        },
      }

      msalInstanceRef.current = new PublicClientApplication(config)
    }

    return msalInstanceRef.current
  }

  const handleSSOAction = async (domain: string): Promise<boolean> => {
    if (window.location.hostname.includes('localhost')) {
      return false
    }

    if (domain.includes('avenue')) {
      await handleAvenueSSOAction()
      return true
    }

    setState({ type: 'loading' })

    try {
      const { url } = await getSsoProvider(domain)

      if (url) {
        setState({ type: 'idle' })
        window.location.assign(url)
        return true
      }

      setState({ type: 'idle' })
      return false
    } catch (error) {
      handleAuthError(error, getTranslation, 'sso-provider-discovery')
      setState({ type: 'error', errorType: 'UNKNOWN' })
      return false
    }
  }

  const handleAvenueSSOAction = async () => {
    try {
      setState({ type: 'loading' })

      const msalInstance = await getMsalInstance()

      await msalInstance.initialize()

      const { accessToken: token } = await msalInstance.loginPopup({
        scopes: ['user.read'],
        prompt: 'select_account',
      })

      if (!token) {
        throw new Error('Msal loginPopup missing token')
      }

      const tokens = await ssoLogin({ token, legacy: true })

      if (!tokens.access_token || !tokens.refresh_token) {
        throw new Error('SSO login response is missing tokens')
      }

      await enterSession(tokens)

      setState({ type: 'success' })
    } catch (error) {
      // Usuário fechou/cancelou o popup: não é erro, apenas volta ao estado ocioso.
      if (isPopupCancelledError(error)) {
        setState({ type: 'error', errorType: 'CLIENT_ERROR' })
        return
      }

      handleAuthError(error, getTranslation, 'avenue-sso-login')
      setState({ type: 'error', errorType: 'UNKNOWN' })
    }
  }

  return {
    state,
    handleSSOAction,
    handleAvenueSSOAction,
  }
}

function isPopupCancelledError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errorCode' in error &&
    (error as { errorCode?: string }).errorCode === 'user_cancelled'
  )
}
